import React, { type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CheckIcon from '@atlaskit/icon/glyph/check';
import Spinner from '@atlaskit/spinner';
import { G300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl/src/index.tsx';
import i18n from '../../../i18n';
import { SUBMIT_INITIATED_STATUS, SUBMIT_COMPLETED_STATUS, type SubmitStatus } from './model';

export type Props = {
	isDisabled: boolean;
	submitStatus: SubmitStatus;
	buttonTextDescriptor: MessageDescriptor;
	onSubmit: (e: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
};

export function SubmitButton(props: Props) {
	const { isDisabled, submitStatus, onSubmit, buttonTextDescriptor } = props;
	const saveComplete = submitStatus === SUBMIT_COMPLETED_STATUS;
	const { formatMessage } = useIntl();
	return (
		<SaveCompleteButton saveComplete={saveComplete}>
			<Button
				appearance="primary"
				isDisabled={isDisabled}
				onClick={submitStatus ? undefined : onSubmit}
				aria-label={!submitStatus ? undefined : formatMessage(buttonTextDescriptor)}
			>
				<TextWrapper isVisible={!submitStatus}>{formatMessage(buttonTextDescriptor)}</TextWrapper>
				<VisibilityAnimation isVisible={submitStatus === SUBMIT_INITIATED_STATUS}>
					<Spinner size="small" appearance="invert" />
				</VisibilityAnimation>
				<VisibilityAnimation isVisible={submitStatus === SUBMIT_COMPLETED_STATUS}>
					{/* @ts-expect-error - TS2769 - No overload matches this call. Caused by 2nd party lib */}
					<CheckIcon primaryColor={token('color.icon.inverse', 'white')} />
				</VisibilityAnimation>
			</Button>
		</SaveCompleteButton>
	);
}

SubmitButton.defaultProps = {
	isDisabled: false,
	submitStatus: null,
	// Text is "Save" by default, but can be overridden
	buttonTextDescriptor: i18n.save,
	onSubmit: noop,
};

// FIXME - remove the default export and use named exports only
export default SubmitButton;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SaveCompleteButton = styled.div<{ saveComplete: boolean }>({
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		// A balancing act between likelihood translations will alter button width
		// when switching to spinner/tick, and the appearance of the button at-rest.
		// This is slightly wider than the button ordinarily would be in English.
		minWidth: token('space.800', '64px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > span': {
			flexGrow: 1,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ saveComplete }) =>
		saveComplete && token('color.background.success.bold', G300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextWrapper = styled.span<{ isVisible: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: ({ isVisible }) => (isVisible ? 'visible' : 'hidden'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VisibilityAnimation = styled.span<{ isVisible: boolean }>({
	position: 'absolute',
	// No Spacing primitive use-case for this sort of absolute positioning

	top: '50%',
	left: 0,
	right: 0,
	transform: 'translateY(-50%)',
	lineHeight: 0,
	transition: 'opacity 0.2s ease-in-out',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ isVisible }) => (isVisible ? '1' : '0'),
});
